/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { FormattedMessage } from '@modules/i18n'
import type { Tag } from '@modules/resources-shared-components/Tags/types'
import type { ValidationErrors } from '@modules/resources-shared-components/Tags/TagsFlyout/AddTagsForm'

export const MAX_DEPLOYMENT_TAGS = 18
export const MAX_DEPLOYMENT_TAG_KEY_LENGTH = 27
export const MAX_DEPLOYMENT_TAG_VALUE_LENGTH = 99

export const validateTag = (tag: Tag, currentTags: Tag[]): ValidationErrors => {
  const { key, value } = tag

  const errors: ValidationErrors = {}

  if (key && key.length > MAX_DEPLOYMENT_TAG_KEY_LENGTH) {
    errors.key_max_length = (
      <FormattedMessage
        id='deployment-tags.flyout.validations.key-length-error'
        defaultMessage="The key can't exceed {max} characters."
        values={{ max: MAX_DEPLOYMENT_TAG_KEY_LENGTH }}
      />
    )
  }

  if (value && value.length > MAX_DEPLOYMENT_TAG_VALUE_LENGTH) {
    errors.value_max_length = (
      <FormattedMessage
        id='deployment-tags.flyout.validations.value-length-error'
        defaultMessage="The value can't exceed {max} characters."
        values={{ max: MAX_DEPLOYMENT_TAG_VALUE_LENGTH }}
      />
    )
  }

  if (currentTags.length === MAX_DEPLOYMENT_TAGS) {
    errors.max_tags = (
      <FormattedMessage
        id='deployment-tags.flyout.validations.max-tags-error'
        defaultMessage='You have reached the limit number of tags for this deployment.'
      />
    )
  }

  if (
    currentTags.some(
      ({ key: currentKey, value: currentValue }) => currentKey === key && currentValue === value,
    )
  ) {
    errors.has_duplicates = (
      <FormattedMessage
        id='deployment-tags.flyout.validations.duplicate-error'
        defaultMessage='This tag already exists. Enter a different tag.'
      />
    )
  }

  if (key && /[:\s]/.test(key)) {
    errors.invalid_key = (
      <FormattedMessage
        id='deployment-tags.flyout.validations.key-invalid-error'
        defaultMessage='The key must not contain colons or spaces.'
      />
    )
  }

  if (value && /\s/.test(value)) {
    errors.invalid_value = (
      <FormattedMessage
        id='deployment-tags.flyout.validations.white-spaces-value-error'
        defaultMessage='The value must not contain spaces.'
      />
    )
  }

  return errors
}
